/**
 * BottomNav — Sprint 4: FAB now opens floating AI Copilot modal
 */

import { useNavigate, useLocation } from 'react-router';
import { Home, Calendar, BookOpen, Compass, Sparkles } from 'lucide-react';
import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { AICopilotModal } from './AICopilotModal';
import { useEventModules } from '../hooks/useEventModules';

type NavigationItem =
  | {
      kind: 'destination';
      label: string;
      icon: typeof Home;
      paths: string[];
      action: () => void;
    }
  | { kind: 'copilot'; label: string };

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [copilotOpen, setCopilotOpen] = useState(false);
  const navigationRef = useRef<HTMLElement>(null);
  const { isEnabled, labelFor } = useEventModules();

  useLayoutEffect(() => {
    const navigation = navigationRef.current;
    const parent = navigation?.parentElement;
    if (!navigation || !parent) return;
    const measure = () => {
      const height = navigation.getBoundingClientRect().height;
      if (height > 0) parent.style.setProperty('--app-bottom-nav-height', `${height}px`);
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : undefined;
    observer?.observe(navigation);
    return () => {
      observer?.disconnect();
      parent.style.removeProperty('--app-bottom-nav-height');
    };
  }, []);

  const isActive = (paths: string[]) =>
    paths.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));

  const hasExploreDestination = (
    ['papers', 'speakers', 'exhibitors', 'sponsors', 'industry_partners', 'committee', 'attendees'] as const
  ).some(isEnabled);
  const navItems: NavigationItem[] = [
    { kind: 'destination', label: 'Home', icon: Home, paths: ['/home', '/profile', '/notifications', '/map', '/event'], action: () => navigate('/home') },
    ...(isEnabled('agenda')
      ? [{ kind: 'destination' as const, label: labelFor('agenda') || 'Agenda', icon: Calendar, paths: ['/agenda', '/session'], action: () => navigate('/agenda') }]
      : []),
    ...(isEnabled('ai_copilot')
      ? [{ kind: 'copilot' as const, label: labelFor('ai_copilot') || 'AI Copilot' }]
      : []),
    { kind: 'destination', label: 'Library', icon: BookOpen, paths: ['/library', '/notes'], action: () => navigate('/library') },
    ...(hasExploreDestination
      ? [{ kind: 'destination' as const, label: 'Explore', icon: Compass, paths: ['/explore', '/speaker', '/paper', '/exhibitor', '/attendee'], action: () => navigate('/explore') }]
      : []),
  ];

  return (
    <>
      <nav
        ref={navigationRef}
        aria-label="Primary navigation"
        className="app-bottom-nav absolute bottom-0 left-0 right-0 backdrop-blur-xl border-t flex items-center justify-around px-2 z-20 transition-colors duration-300"
        style={{
          '--bottom-safe-area': 'max(env(safe-area-inset-bottom, 0px), 1.5rem)',
          backgroundColor: 'var(--app-nav-bg)',
          borderColor: 'var(--app-border)',
          height: 'calc(5rem + var(--bottom-safe-area))',
          paddingBottom: 'var(--bottom-safe-area)',
          boxSizing: 'border-box',
        } as CSSProperties}
      >
        {navItems.map((item) => {
          if (item.kind === 'copilot') {
            return (
              <div key="fab" className="flex min-w-0 flex-1 flex-col items-center">
                <button
                  type="button"
                  aria-label={item.label}
                  onClick={() => setCopilotOpen(true)}
                  className="app-nav-action relative -mt-8 size-14 rounded-full flex items-center justify-center active:scale-90 transition-transform"
                  style={{
                    backgroundColor: 'var(--app-accent-solid)',
                    boxShadow: copilotOpen
                      ? '0 0 40px var(--app-accent-glow), 0 0 80px var(--app-accent-glow)'
                      : '0 0 28px var(--app-accent-glow)',
                  }}
                >
                  <div
                    className="absolute inset-0 rounded-full border-4 transition-colors duration-300"
                    style={{ borderColor: 'var(--app-bg)' }}
                  />
                  <Sparkles className="w-6 h-6 text-white" />
                </button>
                <span
                  className="mt-1 block max-w-full truncate px-1 text-[10px] transition-colors duration-300"
                  title={item.label}
                  style={{ color: copilotOpen ? 'var(--app-accent)' : 'var(--app-text-muted)' }}
                >
                  {item.label}
                </span>
              </div>
            );
          }

          const Icon = item.icon;
          const active = isActive(item.paths);
          return (
            <button
              key={item.label}
              onClick={item.action}
              title={item.label}
              className="app-nav-action flex min-w-0 flex-1 flex-col items-center gap-1 py-2 active:opacity-70 transition-opacity"
            >
              <Icon
                className="w-5 h-5 transition-colors duration-200"
                style={{ color: active ? 'var(--app-accent)' : 'var(--app-text-muted)' }}
              />
              <span
                className="block max-w-full truncate px-1 text-[9px] transition-colors duration-200"
                style={{ color: active ? 'var(--app-accent)' : 'var(--app-text-muted)' }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </nav>

      {/* Sprint 4: AI Copilot floating modal */}
      <AICopilotModal isOpen={copilotOpen} onClose={() => setCopilotOpen(false)} />
    </>
  );
}
